import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AssetSummaryPage } from 'components/Asset/AssetSummaryPage';
import {
  ModalScreenProps,
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, NAVIGATE_MODAL_BACK } from 'store/actions';
import { doAseSaveAssetDetails } from 'store/assessments/actions';
import {
  selectAseAssetId,
  selectAseAssetDetails,
  selectAseAssetAse,
  selectAseTemplateSettings,
  selectAseId,
  selectAseIsOnline,
  selectAseIsEditable,
} from 'store/assessments/selectors';
import { AssessmentResults } from 'store/assessments/types';
import { AppContextEnum } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSESSMENT],
  Screens.ASE_ASSET
>;

export const AssetScreenForNavigation: React.FC<Props> = ({
  navigation,
  route,
}) => <AssetScreen navigation={navigation} route={route} />;

export const AssetScreen: React.FC<
  | ModalScreenProps<ModalScreens.AseAssetDetails>
  | {
      route: Pick<Props['route'], 'params'>;
      navigation: {
        goBack: Props['navigation']['goBack'];
        setOptions: Props['navigation']['setOptions'];
      };
      isModal?: undefined;
    }
> = ({ navigation, isModal }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();
  const assessmentId = useSelector(selectAseId);
  const isEditable = useSelector(selectAseIsEditable);
  const isOnline = useSelector(selectAseIsOnline);
  const assetId = useSelector(selectAseAssetId);
  const asset = useSelector(selectAseAssetDetails);
  const newAsset = useSelector(selectAseAssetAse);
  const features = useSelector(selectAseTemplateSettings);

  const initDataRef = React.useRef<
    Partial<Record<keyof AssessmentResults['asset'], string | null>>
  >({ ...newAsset });
  const dataRef = React.useRef<
    Partial<Record<keyof AssessmentResults['asset'], string | null>>
  >({ ...newAsset });

  const autoSave = !isModal;
  const checkDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  if (autoSave) {
    initDataRef.current = { ...newAsset };
    dataRef.current = { ...newAsset };
  }

  const handleSave = React.useCallback(() => {
    if (checkDirty()) {
      dispatch(
        doAseSaveAssetDetails({
          set: dataRef.current,
        }),
      );
      return;
    }
    if (isModal) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [checkDirty, dispatch, isModal, navigation, screenSize]);

  const handleChanges = React.useCallback(
    (data: Partial<typeof dataRef.current>) => {
      dataRef.current = { ...dataRef.current, ...data };
      if (autoSave) {
        handleSave();
      } else {
        forceUpdate();
      }
    },
    [autoSave, forceUpdate, handleSave],
  );

  const handleClose = React.useCallback(() => {
    if (checkDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => {
          if (isModal) {
            dispatch(NAVIGATE_MODAL_BACK());
          } else if (screenSize === 'small') {
            navigation?.goBack();
          }
        },
      });
      return;
    }
    if (isModal) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [
    checkDirty,
    confirmUnsaved,
    dispatch,
    handleSave,
    isModal,
    navigation,
    screenSize,
  ]);

  const isDirty = checkDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty });
  }, [navigation, isDirty]);
  useEsc(isModal && handleClose);

  const somethingIsEditable =
    isEditable &&
    (!!features.editAssetName ||
      !!features.editAssetCustomReference ||
      !!features.editAssetAddress ||
      (!isOnline && !!features.editAssetAvatar));

  return (
    <AssetSummaryPage
      title="Address & Photo"
      context={{
        context: isOnline
          ? AppContextEnum.remoteAssessment
          : AppContextEnum.localAssessment,
        assetId,
        assessmentId,
      }}
      editable={{
        avatar: isEditable && !!features.editAssetAvatar,
        name:
          isEditable && (!isOnline || !!isModal) && !!features.editAssetName,
        customReference:
          isEditable &&
          (!isOnline || !!isModal) &&
          !!features.editAssetCustomReference,
        address:
          isEditable && (!isOnline || !!isModal) && !!features.editAssetAddress,
      }}
      onChanges={handleChanges}
      onPressBackButton={
        !isModal && screenSize === 'small' ? handleClose : undefined
      }
      onPressCancelButton={isModal ? handleClose : undefined}
      onPressSaveButton={
        !isOnline || !somethingIsEditable
          ? undefined
          : isModal
            ? handleSave
            : () =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.AseAssetDetails,
                    params: {},
                  }),
                )
      }
      onEsc={handleClose}
      saveButtonText={isModal ? 'Save' : 'Edit'}
      details={{
        name: asset.name,
        avatar: asset.avatar,
        reference: asset.reference,
        customReference: asset.customReference,
        parentName: asset.parentName,
        address1: asset.address1,
        address2: asset.address2,
        city: asset.city,
        county: asset.county,
        postcode: asset.postcode,
        phone: asset.phone,
      }}
      detailsNew={{
        name: dataRef.current.name,
        avatar: dataRef.current.avatar,
        customReference: dataRef.current.customReference,
        address1: dataRef.current.address1,
        address2: dataRef.current.address2,
        city: dataRef.current.city,
        county: dataRef.current.county,
        postcode: dataRef.current.postcode,
        phone: dataRef.current.phone,
      }}
    />
  );
};
